import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { allMovies, categories } from '@/mocks/movies';
import Breadcrumb, { getCategoryName } from '@/components/feature/Breadcrumb';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = useMemo(() => allMovies.find((m) => m.id === Number(id)), [id]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50 pt-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '未找到' }]} />
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">影片未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该影片可能已下架或不存在</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const relatedMovies = allMovies
    .filter((m) => m.id !== movie.id && (m.category === movie.category || m.subcategory === movie.subcategory))
    .slice(0, 6);

  const categoryName = getCategoryName(movie.category);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${movie.category}` },
    { label: movie.title },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.image,
    dateCreated: movie.releaseDate,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((a) => ({ '@type': 'Person', name: a })),
    countryOfOrigin: movie.region,
    url: `https://www.51cinema.com/detail/${movie.id}`,
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <article className="mb-10 md:mb-14">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="flex-shrink-0 w-full sm:w-[260px] md:w-[300px] mx-auto md:mx-0">
              <div className="rounded-lg overflow-hidden aspect-[2/3]">
                <img
                  src={movie.image}
                  alt={`${movie.title} - 51影院免费在线观看`}
                  title={`${movie.title} - 51影院 | 久久影院 | 悠久影院`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {movie.title}
              </h1>

              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-full text-sm font-bold bg-accent-500 text-background-50">
                  {movie.rating} 分
                </span>
                <span className="text-sm text-foreground-500">{movie.subcategory}</span>
                <span className="text-sm text-foreground-500">{movie.year}</span>
                <span className="text-sm text-foreground-500">{movie.region}</span>
                <span className="text-sm text-foreground-500">{movie.duration}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-6 p-4 rounded-lg bg-background-100">
                <div className="flex gap-2">
                  <span className="text-xs text-foreground-500 flex-shrink-0 whitespace-nowrap">导演：</span>
                  <span className="text-xs text-foreground-800">{movie.director}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-xs text-foreground-500 flex-shrink-0 whitespace-nowrap">语言：</span>
                  <span className="text-xs text-foreground-800">{movie.language}</span>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <span className="text-xs text-foreground-500 flex-shrink-0 whitespace-nowrap">主演：</span>
                  <span className="text-xs text-foreground-800">{movie.actors.join(' / ')}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-xs text-foreground-500 flex-shrink-0 whitespace-nowrap">状态：</span>
                  <span className="text-xs text-primary-500 font-medium">{movie.updateStatus}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-xs text-foreground-500 flex-shrink-0 whitespace-nowrap">上映：</span>
                  <span className="text-xs text-foreground-800">{movie.releaseDate}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {movie.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <button className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-play-fill mr-1.5"></i>立即播放
                </button>
                <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line mr-1.5"></i>收藏
                </button>
                <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-share-forward-line mr-1.5"></i>分享
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">剧情简介</h2>
            <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
          </div>

          <div className="mt-4 flex items-center gap-4 text-xs text-foreground-500">
            <span><i className="ri-eye-line mr-1"></i>{(movie.viewCount / 10000).toFixed(0)}万次观看</span>
            <span className="text-foreground-400">|</span>
            <span>上映日期：{movie.releaseDate}</span>
          </div>
        </article>

        {relatedMovies.length > 0 && (
          <section className="pb-10 md:pb-14">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                同类推荐
              </h2>
              <Link to={`/category/${movie.category}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                更多{movie.subcategory} <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {relatedMovies.map((m) => (
                <Link key={m.id} to={`/detail/${m.id}`} className="group block cursor-pointer">
                  <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3">
                    <img
                      src={m.image}
                      alt={`${m.title} - 51影院`}
                      title={`${m.title} - 51影院推荐`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500 text-background-50">
                        {m.rating}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                    {m.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{m.subcategory} · {m.year}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}